/**
 * Trip manifest domain contracts — Module 4 (TW-3448).
 *
 * A trip's *manifest* is the set of roster passengers assigned to travel on it: the
 * `trip_passenger` join (data-model §6). The same manifest is co-edited by both portals — the club
 * builds its travelling party from its own roster, and TC can build/edit it too — so these Zod
 * schemas + the progress fold are the single source of truth the API validates with and both the
 * club and back-office builder render from (architecture §4.1).
 *
 * The readiness/progress maths is a pure fold over a roster + the assigned-id set, exactly like the
 * trip/proposal summaries: it never reads the clock (it consumes each row's already-derived
 * {@link DocumentReadiness}) so it is deterministic and unit-testable.
 */
import { z } from "zod";
import type { DocumentReadiness } from "./passenger";

/**
 * Assign a roster passenger to a trip. `roleOnTrip` is optional free text (e.g. "Captain",
 * "Physio") layered on top of the player/staff distinction the passenger already carries — the
 * builder derives the player/staff split from `passenger.isStaff`, this is just an extra label.
 */
export const assignPassengerSchema = z.object({
  passengerId: z.uuid(),
  roleOnTrip: z.string().trim().max(100).nullable().optional(),
});
export type AssignPassengerInput = z.infer<typeof assignPassengerSchema>;

/** A single trip↔passenger assignment as returned to the client (the `trip_passenger` join row). */
export interface TripPassengerDto {
  id: string;
  tripId: string;
  clubId: string;
  passengerId: string;
  roleOnTrip: string | null;
  addedBy: string | null;
  addedAt: string;
}

/** The minimal roster shape the manifest fold needs (a subset of {@link PassengerDto}). */
export interface ManifestRosterRow {
  id: string;
  isStaff: boolean;
  documentStatus: DocumentReadiness;
}

/** Live progress + counts + document-readiness for a trip's manifest (drives the builder bar). */
export interface ManifestSummary {
  /** How many roster members are currently on the list. */
  assignedCount: number;
  /** Players / staff available on the roster (the denominators in the builder's sub-counts). */
  totalPlayers: number;
  totalStaff: number;
  /** Players / staff currently assigned (the numerators). */
  assignedPlayers: number;
  assignedStaff: number;
  /** Assigned members whose document readiness is anything other than `ok` (needs attention). */
  docIssues: number;
  /** The same doc-issue count split by role, for the per-role "documents ready" badges. */
  playerDocIssues: number;
  staffDocIssues: number;
  /** Progress toward the trip's requested `paxCount`, 0–100 (0 when pax is unknown). */
  pct: number;
  /**
   * Whether the manifest is fully built. When the trip carries a requested players/staff
   * breakdown ({@link hasRoleTargets}) this means *both* roles are filled; otherwise it falls
   * back to reaching the requested `paxCount` (false when that's unknown/zero).
   */
  complete: boolean;
  /**
   * The trip's *requested* players/staff breakdown (TW user-testing) — the denominators for the
   * per-role progress bars. Null when the enquiry didn't capture the split (legacy/sparse trips).
   */
  requestedPlayers: number | null;
  requestedStaff: number | null;
  /** True when both requested counts are present, so the UI can show two per-role progress bars. */
  hasRoleTargets: boolean;
  /** Per-role progress toward the requested counts, 0–100 (100 when a role needs nobody). */
  playerPct: number;
  staffPct: number;
  /** Whether each role has reached its requested count (a role requesting 0 is trivially complete). */
  playersComplete: boolean;
  staffComplete: boolean;
}

/**
 * Whether the manifest has been edited since it was last submitted to TC — true only when there
 * *is* a submission and the manifest changed after it. Clock-free: both args are ISO-8601 UTC
 * strings (`toISOString()`), so a lexical compare is also chronological (no `Date` parse, no tz
 * drift), mirroring the calendar fold.
 */
export function manifestModifiedSinceSubmit(
  submittedAt: string | null | undefined,
  manifestUpdatedAt: string | null | undefined,
): boolean {
  return Boolean(submittedAt && manifestUpdatedAt && manifestUpdatedAt > submittedAt);
}

/**
 * Summarise a manifest from the club's roster, the set of assigned passenger ids, and the trip's
 * requested pax count. Pure — pass already-derived per-row readiness in; never reads the clock.
 * Counts are computed against the *roster* (so an assigned id missing from the roster — e.g. a
 * since-removed passenger — simply doesn't count), mirroring the prototype's set-intersection.
 */
export function summarizeManifest(
  roster: ManifestRosterRow[],
  assignedIds: Iterable<string>,
  paxCount: number | null | undefined,
  requested?: { players?: number | null; staff?: number | null },
): ManifestSummary {
  const assigned = assignedIds instanceof Set ? assignedIds : new Set(assignedIds);
  let totalPlayers = 0;
  let totalStaff = 0;
  let assignedPlayers = 0;
  let assignedStaff = 0;
  let assignedCount = 0;
  let playerDocIssues = 0;
  let staffDocIssues = 0;

  for (const row of roster) {
    if (row.isStaff) totalStaff += 1;
    else totalPlayers += 1;
    if (!assigned.has(row.id)) continue;
    assignedCount += 1;
    if (row.isStaff) assignedStaff += 1;
    else assignedPlayers += 1;
    if (row.documentStatus !== "ok") {
      if (row.isStaff) staffDocIssues += 1;
      else playerDocIssues += 1;
    }
  }
  const docIssues = playerDocIssues + staffDocIssues;

  const pax = paxCount ?? 0;
  const pct = pax > 0 ? Math.min(100, Math.round((assignedCount / pax) * 100)) : 0;

  const requestedPlayers = requested?.players ?? null;
  const requestedStaff = requested?.staff ?? null;
  const hasRoleTargets = requestedPlayers != null && requestedStaff != null;
  // A role needing nobody (target 0) is trivially full; an unknown target reads as 0%.
  const rolePct = (assigned: number, target: number | null): number =>
    target == null ? 0 : target <= 0 ? 100 : Math.min(100, Math.round((assigned / target) * 100));
  const playerPct = rolePct(assignedPlayers, requestedPlayers);
  const staffPct = rolePct(assignedStaff, requestedStaff);
  const playersComplete = requestedPlayers != null && assignedPlayers >= requestedPlayers;
  const staffComplete = requestedStaff != null && assignedStaff >= requestedStaff;

  // With a requested breakdown, "done" means every role is filled; otherwise fall back to pax.
  const complete = hasRoleTargets ? playersComplete && staffComplete : pax > 0 && assignedCount >= pax;

  return {
    assignedCount,
    totalPlayers,
    totalStaff,
    assignedPlayers,
    assignedStaff,
    docIssues,
    playerDocIssues,
    staffDocIssues,
    pct,
    complete,
    requestedPlayers,
    requestedStaff,
    hasRoleTargets,
    playerPct,
    staffPct,
    playersComplete,
    staffComplete,
  };
}
